import React from 'react';
import { StyleSheet, Text, View, Image } from 'react-native';
import { useNavigate } from 'react-router-dom';

import {
  PublicUnpaidBillStatus,
  PublicUnpaidBillStatement,
} from 'apollo/queries/autogenerate/schemas-public';
import { formatYen } from 'screens/StatementScreen/utils';
import colors from 'shared/constants/colors';
import Button from 'shared/modules/common/components/atoms/Button';
import { getNumberOfDaysToNow } from 'shared/utils/date';
import routes from 'web/navigation/routes';

type Props = {
  bill?: PublicUnpaidBillStatement;
  disabled: boolean;
  paidyCode: string;
};

const getBillDueDate = (month: number, date: number) => {
  const interpolatedMonth = month + 1;

  return `${interpolatedMonth}月1日 - ${interpolatedMonth}月${date}日`;
};

export default function BillStatementCard({
  bill,
  paidyCode,
  disabled = false,
}: Props) {
  const navigate = useNavigate();
  const isOverdue = bill?.status === PublicUnpaidBillStatus.OVERDUE;

  if (!bill) {
    return null;
  }

  const billingDate = new Date(bill.dueAt);
  const billDueDate = getBillDueDate(
    billingDate.getMonth(),
    billingDate.getDate()
  );

  const handleOnPress = () => {
    navigate(routes.unauthenticated.bill.replace(':paidyCode', paidyCode), {
      state: {
        bill:
          getNumberOfDaysToNow(bill.dueAt) > 120
            ? { billStatement: { ...bill } }
            : undefined,
      },
    });
  };

  return (
    <View style={styles.container}>
      <Text style={styles.period}>
        {`${billingDate.getFullYear()}年${
          billingDate.getMonth() + 1
        }月分のご請求`}
      </Text>
      <View style={styles.amountContainer}>
        <View style={styles.amount}>
          <Text style={styles.outstandingAmount}>
            {formatYen(bill?.outstandingAmount)}
          </Text>
          {isOverdue && (
            <View style={styles.overdueLabelContainer}>
              <Text style={styles.overdueLabel}>お支払い待ち</Text>
            </View>
          )}
        </View>
        <Image
          style={styles.arrow}
          source={require('shared/assets/images/icons/arrow-right.png')}
        />
      </View>
      <View style={styles.paymentInfoContainer}>
        <Text style={styles.paymentInfoTitle}>お支払い期間</Text>
        <Text
          style={[
            styles.paymentInfoDescription,
            !disabled && styles.paymentInfoDescriptionRed,
            styles.paymentPeriod,
          ]}
        >
          {billDueDate}
        </Text>
      </View>
      <View style={styles.paymentInfoContainer}>
        <Text style={styles.paymentInfoTitle}>お支払い方法</Text>
        <Text style={styles.paymentInfoDescription}>
          {getNumberOfDaysToNow(bill.dueAt) > 120
            ? '銀行振込'
            : 'コンビニ払い/銀行振込'}
        </Text>
      </View>
      <View style={styles.payButtonWrapper}>
        <Button
          onPress={handleOnPress}
          disabled={disabled}
          title="お支払い"
          id="payment-button"
          variant="danger"
          style={styles.button}
        />
      </View>
      {disabled && (
        <>
          <Text style={styles.disclaimerRed}>
            先に確定したご請求をお支払いください。
          </Text>
          <Text style={styles.disclaimerRegular}>
            先に確定したご請求のお支払い後、この画面を更新してください。お支払いの確認には、時間がかかる場合があります。
          </Text>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    padding: 24,
    backgroundColor: colors.white,
    marginTop: 24,
  },
  period: {
    fontFamily: 'Hiragino Sans',
    fontSize: 13,
    color: colors.greyDark,
    fontWeight: '300',
    marginBottom: 24,
  },
  amountContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.greyLight,
    paddingBottom: 24,
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  amount: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  outstandingAmount: {
    fontFamily: 'Hiragino Sans',
    fontWeight: '700',
    fontSize: 28,
    color: colors.greyDark,
    marginRight: 16,
  },
  overdueLabelContainer: {
    borderRadius: 4,
    backgroundColor: colors.red,
    padding: 4,
  },
  overdueLabel: {
    fontFamily: 'Hiragino Sans',
    fontSize: 13,
    color: colors.white,
    fontWeight: '600',
  },
  arrow: {
    width: 24,
    height: 24,
  },
  paymentInfoContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  paymentInfoTitle: {
    fontFamily: 'Hiragino Sans',
    fontSize: 13,
    color: colors.greyDark,
    fontWeight: '300',
  },
  paymentInfoDescription: {
    fontFamily: 'Hiragino Sans',
    fontSize: 13,
    color: colors.greyNavTitle,
    fontWeight: '600',
  },
  paymentInfoDescriptionRed: {
    color: colors.red,
  },
  disclaimerRed: {
    fontFamily: 'Hiragino Sans',
    fontSize: 13,
    color: colors.red,
    fontWeight: '300',
    marginTop: 16,
    marginBottom: 8,
  },
  disclaimerRegular: {
    fontFamily: 'Hiragino Sans',
    fontSize: 13,
    color: colors.greyDark,
    fontWeight: '300',
  },
  button: {
    marginTop: 24,
    maxWidth: 296,
  },
  paymentPeriod: {
    marginBottom: 24,
  },
  payButtonWrapper: {
    alignItems: 'center',
  },
});
